/**
 * Getting started — the shared pieces.
 *
 * Three surfaces, one query:
 *
 *   OnboardingNextCard   on the Dashboard. Leads with the SINGLE next action,
 *                        because a list of eleven outstanding items is a thing
 *                        that stops people and one named sentence is a thing
 *                        that moves them. Dismissible.
 *   OnboardingProgress   a compact pill for the Shell, so the way back is
 *                        always visible. Exported for the PM to place; this
 *                        file does not touch Shell.tsx.
 *   OnboardingChecklist  the full list, used by the /getting-started page.
 *
 * TONE
 * No confetti, no badges, no streaks, no exclamation marks. These are
 * professionals discharging a legal obligation; the right register for a
 * finished step is a quiet "Done" and nothing else. See the note at the top of
 * lib/onboardingStrings.ts.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useApiError } from '../lib/useApiError';
import { useOnboardingT } from '../lib/onboardingStrings';
import { Button, Card, Pill, ProgressBar, Skeleton, ErrorNotice, type Tone } from './ui';
import { IconChecklist, IconChevron } from './icons';
import { cn } from '../lib/cn';
import type { OnboardingGoal, OnboardingStepState } from '@opencomply/shared';

// ---------------------------------------------------------------------------
//  Data
// ---------------------------------------------------------------------------

export interface OnboardingStep {
  key: string;
  state: OnboardingStepState;
  href: string;
  detailKey: string | null;
  vars: Record<string, string | number>;
  blockedBy: string | null;
}

export interface OnboardingView {
  goal: OnboardingGoal | null;
  effectiveGoal: OnboardingGoal;
  options: Array<{ goal: OnboardingGoal; recommended: boolean }>;
  canChooseGoal: boolean;
  askGoal: boolean;
  dismissedAt: string | null;
  steps: OnboardingStep[];
  nextStep: OnboardingStep | null;
  progress: { done: number; total: number };
  complete: boolean;
  references: Array<{ key: string; href: string }>;
}

export const ONBOARDING_QUERY_KEY = ['onboarding'] as const;

export function useOnboarding() {
  return useQuery({
    queryKey: ONBOARDING_QUERY_KEY,
    queryFn: () => api.get<OnboardingView>('/onboarding'),
    /**
     * Short, not zero. Every number on this list is derived from records the
     * user is actively changing in another tab, so a long cache would show them
     * a stale tick — the exact failure the whole feature is built to avoid.
     */
    staleTime: 15_000,
  });
}

// ---------------------------------------------------------------------------
//  Presentation helpers
// ---------------------------------------------------------------------------

const STATE_TONE: Record<OnboardingStepState, Tone> = {
  DONE: 'good',
  PARTIAL: 'warning',
  TODO: 'neutral',
  BLOCKED: 'neutral',
};

/**
 * The step's title, allowing for the two steps whose wording depends on whether
 * a framework could be named. "Licence " with nothing after it is worse than
 * the generic sentence, so the generic sentence is a separate key rather than
 * an interpolation that might come out empty.
 */
export function useStepText(siblings: OnboardingStep[] = []) {
  const { tx, txn } = useOnboardingT();

  const title = (step: OnboardingStep): string => {
    const base = `onboarding.step.${step.key}`;
    const framework = step.vars.framework;
    if ((step.key === 'framework.license' || step.key === 'assessment.complete') && !framework) {
      return tx(`${base}.titleAny`);
    }
    return tx(`${base}.title`, step.vars);
  };

  const why = (step: OnboardingStep): string => tx(`onboarding.step.${step.key}.why`);

  const detail = (step: OnboardingStep): string | null => {
    if (step.state === 'BLOCKED' && step.blockedBy) {
      // Never a mute grey row. Say which step is in the way, BY ITS OWN NAME —
      // resolved from the sibling row rather than re-rendered from this one, so
      // the framework in "Complete the ISO/IEC 27001:2022 assessment" is the
      // one that step is actually about and never an empty interpolation.
      const blocker = siblings.find((s) => s.key === step.blockedBy);
      return tx('onboarding.blockedBy', {
        step: blocker ? title(blocker) : tx(`onboarding.step.${step.blockedBy}.titleAny`),
      });
    }
    if (!step.detailKey) return null;
    return txn(`onboarding.step.${step.key}.${step.detailKey}`, step.vars);
  };

  return { title, why, detail };
}

// ---------------------------------------------------------------------------
//  One row
// ---------------------------------------------------------------------------

export function OnboardingStepRow({ step, index, siblings }: {
  step: OnboardingStep;
  index: number;
  /** The whole list, so a blocked row can name its blocker exactly. */
  siblings: OnboardingStep[];
}) {
  const { tx } = useOnboardingT();
  const { title, why, detail } = useStepText(siblings);
  const detailText = detail(step);
  const done = step.state === 'DONE';

  return (
    <li className="py-4 first:pt-0 last:pb-0">
      <div className="flex gap-3">
        <StepMarker state={step.state} index={index} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <p className={cn('font-medium text-ink', done && 'text-ink-secondary')}>
              {title(step)}
            </p>
            <Pill tone={STATE_TONE[step.state]}>{tx(`onboarding.state.${step.state}`)}</Pill>
          </div>

          {/* The "why" is dropped once a step is finished. Explaining why
              someone should have done the thing they have already done is
              noise, and it is what makes a long checklist unreadable. */}
          {!done && <p className="oc-muted text-sm mt-1">{why(step)}</p>}

          {detailText && (
            <p
              className={cn(
                'text-sm mt-1.5 tabular-nums',
                step.state === 'DONE' ? 'text-good' : 'text-ink-secondary',
              )}
            >
              {detailText}
            </p>
          )}

          {/* The link stays live even when blocked: a dead row teaches nobody
              anything, and someone who wants to look ahead should be able to. */}
          <Link
            to={step.href}
            className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-brand hover:underline"
          >
            {tx('onboarding.open')}
            <IconChevron className="w-4 h-4 -rotate-90" aria-hidden />
          </Link>
        </div>
      </div>
    </li>
  );
}

/** The tick, or the step number. Shape carries the state as well as colour. */
function StepMarker({ state, index }: { state: OnboardingStepState; index: number }) {
  if (state === 'DONE') {
    return (
      <span
        className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-good/15 text-good grid place-items-center"
        aria-hidden
      >
        <svg viewBox="0 0 20 20" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M4 10.5l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  return (
    <span
      className={cn(
        'mt-0.5 shrink-0 w-6 h-6 rounded-full grid place-items-center text-[11px] font-semibold tabular-nums',
        state === 'PARTIAL' ? 'bg-warning/15 text-warning' : 'bg-ink/[0.07] text-ink-muted',
      )}
      aria-hidden
    >
      {index + 1}
    </span>
  );
}

// ---------------------------------------------------------------------------
//  The full checklist
// ---------------------------------------------------------------------------

export function OnboardingChecklist({ data }: { data: OnboardingView }) {
  const { tx } = useOnboardingT();

  if (data.steps.length === 0) {
    return <p className="oc-muted text-sm">{tx('onboarding.noSteps')}</p>;
  }

  return (
    <ul className="divide-y divide-line">
      {data.steps.map((step, i) => (
        <OnboardingStepRow key={step.key} step={step} index={i} siblings={data.steps} />
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
//  Progress
// ---------------------------------------------------------------------------

export function OnboardingProgressBar({ data }: { data: OnboardingView }) {
  const { tx } = useOnboardingT();
  const pct =
    data.progress.total === 0
      ? 0
      : Math.round((data.progress.done / data.progress.total) * 100);

  return (
    <div className="space-y-1.5">
      <ProgressBar
        value={pct}
        tone={data.complete ? 'good' : 'brand'}
        label={tx('onboarding.title')}
      />
      <p className="text-xs text-ink-muted tabular-nums">
        {tx('onboarding.progress', { done: data.progress.done, total: data.progress.total })}
      </p>
    </div>
  );
}

/**
 * The persistent entry point, for the Shell.
 *
 * Renders nothing at all once everything is finished AND the person has put it
 * away — but note it keeps rendering while complete if they have not, because
 * "that's done" is worth seeing once. It never nags: there is no badge count
 * and no red dot.
 *
 * SHELL PLACEMENT: this is intended to sit directly above `OrganizationCard`
 * in the desktop sidebar, and inside the "More" sheet on a phone. It is a
 * `Link`, so it needs no props.
 */
export function OnboardingNavProgress({ className }: { className?: string }) {
  const { tx } = useOnboardingT();
  const { data } = useOnboarding();

  if (!data) return null;
  if (data.dismissedAt && data.complete) return null;
  if (data.progress.total === 0) return null;

  const pct = Math.round((data.progress.done / data.progress.total) * 100);

  return (
    <Link
      to="/getting-started"
      className={cn(
        'block px-3 py-2.5 rounded-xl bg-surface-sunken hover:bg-ink/[0.05] transition-colors',
        className,
      )}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <IconChecklist className="w-4 h-4 text-ink-muted shrink-0" aria-hidden />
        <span className="text-xs font-medium text-ink-secondary truncate">
          {tx('onboarding.title')}
        </span>
        <span className="ml-auto text-[11px] tabular-nums text-ink-muted">
          {data.progress.done}/{data.progress.total}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-ink/[0.08] overflow-hidden">
        <div
          className={cn('h-full rounded-full', data.complete ? 'bg-good' : 'bg-brand')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
//  The dashboard card
// ---------------------------------------------------------------------------

/**
 * "Do this next" — one action, named, with the reason.
 *
 * Shown on the Dashboard and nowhere else. Returns null when the person has
 * dismissed it, so the dashboard they asked for is the dashboard they get.
 */
export function OnboardingNextCard() {
  const { tx } = useOnboardingT();
  const { data } = useOnboarding();
  const { title, why, detail } = useStepText(data?.steps ?? []);
  const queryClient = useQueryClient();

  const dismiss = useMutation({
    mutationFn: () => api.post<{ dismissedAt: string | null }>('/onboarding/dismiss'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY }),
  });

  if (!data) return null;
  if (data.dismissedAt) return null;
  if (data.progress.total === 0) return null;

  // The goal has never been answered: ask, rather than guessing at a plan and
  // presenting the guess as advice.
  if (data.askGoal) {
    return (
      <Card className="border-brand/30">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-semibold text-ink">{tx('onboarding.ask.title')}</h2>
            <p className="oc-muted text-sm mt-1 max-w-prose">{tx('onboarding.ask.intro')}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/getting-started">
              <Button variant="primary" size="sm">{tx('onboarding.ask.save')}</Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => dismiss.mutate()}
              disabled={dismiss.isPending}
            >
              {tx('onboarding.ask.skip')}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (data.complete) {
    return (
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {/* A plain sentence. No celebration — see the tone note above. */}
            <h2 className="font-semibold text-ink">{tx('onboarding.complete.title')}</h2>
            <p className="oc-muted text-sm mt-1 max-w-prose">{tx('onboarding.complete.body')}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => dismiss.mutate()}
            disabled={dismiss.isPending}
          >
            {tx('onboarding.dismiss')}
          </Button>
        </div>
      </Card>
    );
  }

  const next = data.nextStep;

  return (
    <Card className="border-brand/30">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <p className="text-[11px] uppercase tracking-wide text-brand font-semibold">
          {tx('onboarding.next.title')}
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => dismiss.mutate()}
          disabled={dismiss.isPending}
        >
          {tx('onboarding.dismiss')}
        </Button>
      </div>

      {next ? (
        <div className="space-y-2">
          <p className="text-lg font-semibold text-ink">{title(next)}</p>
          <p className="oc-muted text-sm max-w-prose">{why(next)}</p>
          {detail(next) && (
            <p className="text-sm text-ink-secondary tabular-nums">{detail(next)}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Link to={next.href}>
              <Button variant="primary" size="sm">{tx('onboarding.open')}</Button>
            </Link>
            <Link to="/getting-started">
              <Button variant="ghost" size="sm">{tx('onboarding.viewAll')}</Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="oc-muted text-sm">{tx('onboarding.next.allBlocked')}</p>
          <Link to="/getting-started">
            <Button variant="secondary" size="sm">{tx('onboarding.viewAll')}</Button>
          </Link>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-line">
        <OnboardingProgressBar data={data} />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
//  Goal chooser
// ---------------------------------------------------------------------------

/**
 * The goal question.
 *
 * `recommended` goals come first and the rest sit behind a plain heading rather
 * than being hidden: a partner IS also a company and may genuinely want to
 * certify itself. Leading them with "assess yourself against NIS2" would be
 * wrong; refusing to let them say it would be wronger.
 */
export function OnboardingGoalChooser({
  data,
  onDone,
}: {
  data: OnboardingView;
  onDone?: () => void;
}) {
  const { tx } = useOnboardingT();
  const describeError = useApiError();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<OnboardingGoal | null>(data.goal);

  const save = useMutation({
    mutationFn: (goal: OnboardingGoal) => api.put<OnboardingView>('/onboarding/goal', { goal }),
    onSuccess: (next) => {
      queryClient.setQueryData(ONBOARDING_QUERY_KEY, next);
      void queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY });
      onDone?.();
    },
  });

  const dismiss = useMutation({
    mutationFn: () => api.post('/onboarding/dismiss'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY });
      onDone?.();
    },
  });

  const [recommended, others] = useMemo(
    () => [data.options.filter((o) => o.recommended), data.options.filter((o) => !o.recommended)],
    [data.options],
  );

  if (!data.canChooseGoal) {
    return <p className="oc-muted text-sm">{tx('onboarding.ask.readOnly')}</p>;
  }

  return (
    <div className="space-y-4">
      <p className="oc-muted text-sm max-w-prose">{tx('onboarding.ask.intro')}</p>

      <div className="grid gap-2 sm:grid-cols-2">
        {recommended.map((o) => (
          <GoalOption
            key={o.goal}
            goal={o.goal}
            selected={selected === o.goal}
            onSelect={() => setSelected(o.goal)}
          />
        ))}
      </div>

      {others.length > 0 && (
        <details className="group">
          <summary className="cursor-pointer text-sm font-medium text-ink-secondary hover:text-ink">
            {tx('onboarding.ask.otherGoals')}
          </summary>
          <div className="grid gap-2 sm:grid-cols-2 mt-2">
            {others.map((o) => (
              <GoalOption
                key={o.goal}
                goal={o.goal}
                selected={selected === o.goal}
                onSelect={() => setSelected(o.goal)}
              />
            ))}
          </div>
        </details>
      )}

      {save.error && <ErrorNotice message={describeError(save.error)} />}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          disabled={!selected || save.isPending}
          onClick={() => selected && save.mutate(selected)}
        >
          {tx('onboarding.ask.save')}
        </Button>
        {/* Skipping is a first-class option. Someone who wants to look around
            before committing to a regulatory obligation must be able to. */}
        {data.goal === null && (
          <Button variant="ghost" onClick={() => dismiss.mutate()} disabled={dismiss.isPending}>
            {tx('onboarding.ask.skip')}
          </Button>
        )}
        {data.goal !== null && onDone && (
          <Button variant="ghost" onClick={onDone}>{tx('onboarding.goal.cancel')}</Button>
        )}
      </div>
    </div>
  );
}

function GoalOption({ goal, selected, onSelect }: {
  goal: OnboardingGoal;
  selected: boolean;
  onSelect: () => void;
}) {
  const { tx } = useOnboardingT();
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'text-left rounded-xl border p-3 transition-colors',
        selected
          ? 'border-brand bg-brand/[0.06]'
          : 'border-line hover:border-ink/20 hover:bg-surface-sunken',
      )}
    >
      <p className="font-medium text-ink text-sm">{tx(`onboarding.goal.${goal}.title`)}</p>
      <p className="oc-muted text-xs mt-0.5">{tx(`onboarding.goal.${goal}.body`)}</p>
    </button>
  );
}

// ---------------------------------------------------------------------------
//  Skeleton
// ---------------------------------------------------------------------------

export function OnboardingSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-24" />
      <Skeleton className="h-64" />
    </div>
  );
}
